import type { ChatMessage } from '../useChatMessages/types';
import { ChatMessagePayloadType, ChatMessageStatus } from '../useChatMessages/types';
import type { Chat } from '../useChats/types';
import { ChatCategory } from '../useChats/types';
import type { ChatMedia, ChatMediaInfoMap, ChatMediaInfoPartial } from './types';
import { genChatMedia, genChatMediaInfo } from './utils';

type ChatMediaActions =
  | {
      type: 'openMediaModal' | 'openMediaNotice' | 'mediaModalZoomIn';
      payload: boolean;
    }
  | {
      type: 'activeChatMedia';
      payload: string | undefined;
    }
  | {
      type: 'setChatMediaInfo';
      payload: ChatMediaInfoMap;
    }
  | {
      type: 'getChatMediaInfo';
      payload: string;
      cb: (info: ChatMediaInfoPartial | undefined) => void;
    }
  | {
      type: 'getActivatedChatMediaInfo';
      cb: (info: ChatMediaInfoPartial | undefined) => void;
    }
  | {
      type: 'exitChatMedia';
    }
  | {
      type: 'clearChatMediaInfo';
      payload: string;
    };

function reducer(state: ChatMedia, action: ChatMediaActions): ChatMedia {
  let statePartial: Partial<ChatMedia> | undefined;

  switch (action.type) {
    case 'openMediaModal':
    case 'openMediaNotice':
    case 'mediaModalZoomIn':
      statePartial = { [action.type]: action.payload };
      break;

    case 'activeChatMedia':
      statePartial = { activatedId: action.payload };
      break;

    case 'setChatMediaInfo': {
      const map = {
        ...state.chatMediaInfoMap,
      };
      for (const k in action.payload) {
        map[k] = genChatMediaInfo(map[k], action.payload[k]);
      }
      statePartial = {
        chatMediaInfoMap: map,
      };
      break;
    }

    case 'getChatMediaInfo':
      action.cb(state.chatMediaInfoMap[action.payload]);
      break;

    case 'getActivatedChatMediaInfo':
      action.cb(state.chatMediaInfoMap[state.activatedId!]);
      break;

    case 'exitChatMedia': {
      statePartial = {
        activatedId: undefined,
        openMediaModal: false,
        openMediaNotice: false,
        mediaModalZoomIn: false,
      };

      const chatMediaInfo = state.chatMediaInfoMap[state.activatedId!];

      if (chatMediaInfo) {
        statePartial.chatMediaInfoMap = {
          ...state.chatMediaInfoMap,
          [state.activatedId!]: {
            ...chatMediaInfo,
            status: 0,
            owner_id: '',
          },
        };
      }

      break;
    }

    case 'clearChatMediaInfo': {
      const chatMediaInfoMap = {
        ...state.chatMediaInfoMap,
      };
      delete chatMediaInfoMap[action.payload];

      statePartial = {
        ...state,
        chatMediaInfoMap,
      };

      if (state.activatedId === action.payload) {
        statePartial.activatedId = undefined;
        statePartial.openMediaModal = false;
        statePartial.openMediaNotice = false;
        statePartial.mediaModalZoomIn = false;
      }
      break;
    }
  }

  const state_ = statePartial ? { ...state, ...statePartial } : state;

  return state_;
}

const initialState = genChatMedia();

export function useChatMediaCall() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const chatMediaInfo = useMemo(
    () => (state.activatedId ? state.chatMediaInfoMap[state.activatedId] : undefined),
    [state.activatedId, state.chatMediaInfoMap]
  );

  const setOpenMediaModal = useCallback((open: boolean) => {
    dispatch({ type: 'openMediaModal', payload: open });
  }, []);

  const setOpenMediaNotice = useCallback((open: boolean) => {
    dispatch({ type: 'openMediaNotice', payload: open });
  }, []);

  const setMediaModalZoomIn = useCallback((open: boolean) => {
    dispatch({ type: 'mediaModalZoomIn', payload: open });
  }, []);

  const activeChatMedia = useCallback((id: string | undefined) => {
    dispatch({ type: 'activeChatMedia', payload: id });
  }, []);

  const setChatMediaInfo = useCallback((payload: ChatMediaInfoMap) => {
    dispatch({ type: 'setChatMediaInfo', payload });
  }, []);

  const getChatMediaInfo = useCallback((id: string) => {
    return new Promise<ChatMediaInfoPartial | undefined>((resolve) =>
      dispatch({ type: 'getChatMediaInfo', payload: id, cb: resolve })
    );
  }, []);

  const getActivatedChatMediaInfo = useCallback(() => {
    return new Promise<ChatMediaInfoPartial | undefined>((resolve) =>
      dispatch({ type: 'getActivatedChatMediaInfo', cb: resolve })
    );
  }, []);

  const exitChatMedia = useCallback(() => {
    dispatch({ type: 'exitChatMedia' });
  }, []);

  const clearChatMediaInfo = useCallback((id: string) => {
    dispatch({ type: 'clearChatMediaInfo', payload: id });
  }, []);

  const isMediaPayloadTypeStatus = useCallback((payload_type: number | undefined) => {
    return (
      ChatMessageStatus.CALL_HANG_UP <= Number(payload_type) &&
      Number(payload_type) <= ChatMessageStatus.CALL_PUT_THROUGH
    );
  }, []);

  const isMediaPayloadTypeCall = useCallback((payload_type: number | undefined) => {
    return (
      ChatMessagePayloadType.MEDIA_AUDIO_CALL === payload_type ||
      ChatMessagePayloadType.MEDIA_VIDEO_CALL === payload_type
    );
  }, []);

  const isMediaPayloadTypeEnd = useCallback((payload_type: number | undefined) => {
    return (
      ChatMessageStatus.CALL_HANG_UP <= Number(payload_type) &&
      Number(payload_type) <= ChatMessageStatus.CALL_NETWORK_ERROR
    );
  }, []);

  const getMediaStatus = useCallback(
    (
      chatMediaInfo: ChatMediaInfoPartial | undefined,
      message: ChatMessage,
      status: ChatMessageStatus,
      chat?: Chat
    ) => {
      const status_ =
        chat?.category === ChatCategory.GROUP
          ? 'group'
          : chatMediaInfo &&
              ((chatMediaInfo.message_id &&
                chatMediaInfo.message_id === message.message_id) ||
                (chatMediaInfo.cli_msg_id &&
                  chatMediaInfo.cli_msg_id === message.cli_msg_id))
            ? chatMediaInfo.status
            : isMediaPayloadTypeEnd(status)
              ? status
              : ChatMessageStatus.CALL_NETWORK_ERROR;
      return status_;
    },
    [isMediaPayloadTypeEnd]
  );

  const value = useMemo(
    () => ({
      ...state,
      chatMediaInfo,
      setOpenMediaModal,
      setOpenMediaNotice,
      setMediaModalZoomIn,
      activeChatMedia,
      setChatMediaInfo,
      isMediaPayloadTypeStatus,
      isMediaPayloadTypeCall,
      clearChatMediaInfo,
      getChatMediaInfo,
      getActivatedChatMediaInfo,
      isMediaPayloadTypeEnd,
      getMediaStatus,
      exitChatMedia,
    }),
    [
      state,
      chatMediaInfo,
      setOpenMediaModal,
      setOpenMediaNotice,
      setMediaModalZoomIn,
      activeChatMedia,
      setChatMediaInfo,
      isMediaPayloadTypeStatus,
      isMediaPayloadTypeCall,
      clearChatMediaInfo,
      getChatMediaInfo,
      getActivatedChatMediaInfo,
      isMediaPayloadTypeEnd,
      getMediaStatus,
      exitChatMedia,
    ]
  );

  return value;
}
